'use client'
import { publicEnv } from '@/shared/config/publicEnv'
import { Spinner } from '@hello-aria/ui/spinner'
import { useTranslations } from 'next-intl'
import type { ReactNode } from 'react'
import '@/assets/styles/global.css'
import ErrorBoundary from '@/shared/components/common/AsyncWrapper/ErrorBoundary'
import { useCompany } from '@/shared/hooks/useCompanies'
import { useMe } from '@/shared/hooks/useMe'
import Sidebar from '@/shared/components/Sidebar/Sidebar'
import SandboxBanner from '../components/SandboxBanner'
import { ToastProvider } from '../components/Toaster/ToasterProvider'

type RootLayoutProps = {
    children: ReactNode
}

const RootLayout = ({ children }: RootLayoutProps) => {
    const t = useTranslations('root')
    const { isGod, isLoading } = useMe()
    const company = useCompany()

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen w-screen">
                <Spinner label={t('loading.app')} />
            </div>
        )
    }

    const isSandbox = String(publicEnv.sandbox) === 'true'
    const layoutHeight = isSandbox ? `h-[calc(100vh-35px)]` : 'min-h-screen max-h-screen'
    return (
        <div className="flex flex-col h-screen">
            {isSandbox && <SandboxBanner />}
            <div className={`flex bg-backgroundSecondary grow ${layoutHeight}`}>
                <div className="grow bg-backgroundSecondary" />
                <div className="flex w-full max-w-412.5">
                    <aside className="min-w-62.5 w-62.5 bg-backgroundSecondary overflow-y-auto">
                        <Sidebar company={company.data} isLoading={company.isLoading} isGod={isGod} />
                    </aside>
                    <main className="grow bg-backgroundPrimary py-6 px-8 overflow-y-auto">
                        <ErrorBoundary>
                            <ToastProvider>
                                <div className="h-full">{children}</div>
                            </ToastProvider>
                        </ErrorBoundary>
                    </main>
                </div>
                <div className="grow bg-backgroundPrimary" />
            </div>
        </div>
    )
}

export default RootLayout
